/**
 * CsvImportEditor — opens after a CSV upload, lets the user fix bad cells inline,
 * Validate to re-check, and Commit to insert (all-or-nothing).
 *
 * Props:
 *  - open:           controlled
 *  - onOpenChange:   (bool) => void
 *  - formId:         form id
 *  - initial:        { rows, errors, unknown_columns, fields, row_count } from POST /csv-import
 *  - onCommitted:    () => void   (called after successful insert; parent should reload records)
 */
import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Warning, CheckCircle, ArrowsClockwise, UploadSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const MIN_COL_WIDTH = 160;

export default function CsvImportEditor({ open, onOpenChange, formId, initial, onCommitted }) {
    const [rows, setRows] = useState([]);
    const [errors, setErrors] = useState([]);
    const [fields, setFields] = useState([]);
    const [unknownCols, setUnknownCols] = useState([]);
    const [validating, setValidating] = useState(false);
    const [importing, setImporting] = useState(false);

    useEffect(() => {
        if (open && initial) {
            // Assign a stable per-row key so React reconciliation keeps input focus when
            // the user drops a mid-list row (drop-row shifts array indices otherwise).
            let seq = 0;
            const keyed = (initial.rows || []).map((r) => ({ ...r, __key: `csv-${seq++}` }));
            setRows(keyed);
            setErrors(initial.errors || []);
            setFields(initial.fields || []);
            setUnknownCols(initial.unknown_columns || []);
        }
    }, [open, initial]);

    // index → {field → message}
    const errorMap = useMemo(() => {
        const m = new Map();
        for (const e of errors) {
            const i = e.index ?? (typeof e.row === "number" ? e.row - 2 : null);
            if (i == null || i < 0) continue;
            if (!m.has(i)) m.set(i, {});
            m.get(i)[e.field] = e.message;
        }
        return m;
    }, [errors]);

    const errorCount = errors.length;
    const cleanRows = rows.length - errorMap.size;

    const updateCell = (rowIndex, fieldName, value) => {
        setRows((rs) => {
            const next = [...rs];
            next[rowIndex] = { ...next[rowIndex], [fieldName]: value };
            return next;
        });
        // Optimistically clear that one cell's error so the red highlight goes away
        setErrors((errs) => errs.filter((e) => !((e.index ?? e.row - 2) === rowIndex && e.field === fieldName)));
    };

    const deleteRow = (rowIndex) => {
        setRows((rs) => rs.filter((_, i) => i !== rowIndex));
        // Re-index errors after removal so highlights stay aligned. Easiest: drop all errors and force a revalidate.
        setErrors([]);
    };

    const _payloadRows = () => rows.map(({ __key, ...rest }) => rest); // strip client-only key

    const revalidate = async () => {
        setValidating(true);
        try {
            const { data } = await api.post(`/forms/${formId}/records/csv-commit`, {
                rows: _payloadRows(), validate_only: true,
            });
            setErrors(data.errors || []);
            if (data.ok) toast.success("All rows pass validation. Ready to import.");
            else toast.message(`${(data.errors || []).length} error${(data.errors || []).length === 1 ? "" : "s"} remaining`);
        } catch (e) { toast.error(extractError(e)); }
        finally { setValidating(false); }
    };

    const commit = async () => {
        if (errorCount > 0) {
            toast.error("Fix all errors before importing");
            return;
        }
        setImporting(true);
        try {
            const { data } = await api.post(`/forms/${formId}/records/csv-commit`, {
                rows: _payloadRows(), validate_only: false,
            });
            if (data.ok) {
                toast.success(`Imported ${data.imported_count} record${data.imported_count === 1 ? "" : "s"}`);
                onOpenChange(false);
                onCommitted?.();
            } else {
                setErrors(data.errors || []);
                toast.error(`Import blocked — ${(data.errors || []).length} validation error${(data.errors || []).length === 1 ? "" : "s"}`);
            }
        } catch (e) { toast.error(extractError(e)); }
        finally { setImporting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="rounded-sm max-w-[95vw] w-[95vw] max-h-[92vh] overflow-hidden flex flex-col"
                data-testid="csv-editor-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        Review & correct CSV import
                        <Badge variant="outline" className="rounded-sm font-mono text-[10px]">
                            {rows.length} row{rows.length === 1 ? "" : "s"}
                        </Badge>
                        {errorCount > 0 ? (
                            <Badge variant="outline" className="rounded-sm font-mono text-[10px] border-destructive text-destructive">
                                <Warning size={10} className="mr-1" /> {errorCount} error{errorCount === 1 ? "" : "s"}
                            </Badge>
                        ) : (
                            <Badge variant="outline" className="rounded-sm font-mono text-[10px] border-emerald-600 text-emerald-700">
                                <CheckCircle size={10} className="mr-1" /> All valid
                            </Badge>
                        )}
                    </DialogTitle>
                </DialogHeader>

                {unknownCols.length > 0 && (
                    <div className="mb-2 text-[11px] text-muted-foreground font-mono" data-testid="csv-editor-unknown">
                        Ignored unknown columns: {unknownCols.join(", ")}
                    </div>
                )}

                <div className="flex-1 overflow-auto border border-border rounded-sm" data-testid="csv-editor-table-wrap">
                    <table className="text-sm border-collapse" style={{ minWidth: "max-content" }}>
                        <thead className="sticky top-0 z-10 bg-secondary">
                            <tr>
                                <th className="data-header text-left px-2 py-1.5 w-12 sticky left-0 bg-secondary z-20">#</th>
                                {fields.map((f) => (
                                    <th
                                        key={f.field_name}
                                        className="data-header text-left px-2 py-1.5 whitespace-nowrap"
                                        style={{ minWidth: MIN_COL_WIDTH }}
                                        data-testid={`csv-editor-th-${f.field_name}`}
                                    >
                                        {f.field_label}
                                        {f.required && <span className="text-destructive ml-0.5">*</span>}
                                        <span className="ml-1 text-[9px] font-mono opacity-50">· {f.field_type}</span>
                                    </th>
                                ))}
                                <th className="data-header w-10 sticky right-0 bg-secondary z-20"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, ri) => {
                                const rowErrors = errorMap.get(ri) || {};
                                const isBad = Object.keys(rowErrors).length > 0;
                                return (
                                    <tr key={row.__key || ri} className={isBad ? "bg-destructive/5" : ""} data-testid={`csv-editor-row-${ri}`}>
                                        <td className="data-cell font-mono text-[10px] text-muted-foreground sticky left-0 bg-card z-10">{ri + 1}</td>
                                        {fields.map((f) => {
                                            const err = rowErrors[f.field_name];
                                            const val = row[f.field_name] ?? "";
                                            return (
                                                <td
                                                    key={f.field_name}
                                                    className={`p-0 align-top ${err ? "bg-destructive/10" : ""}`}
                                                    style={{ minWidth: MIN_COL_WIDTH }}
                                                    title={err || ""}
                                                    data-testid={`csv-editor-cell-${ri}-${f.field_name}`}
                                                >
                                                    <CellEditor
                                                        field={f}
                                                        value={val}
                                                        onChange={(v) => updateCell(ri, f.field_name, v)}
                                                        invalid={!!err}
                                                    />
                                                    {err && (
                                                        <div className="px-2 py-0.5 text-[10px] text-destructive font-mono leading-tight" data-testid={`csv-editor-err-${ri}-${f.field_name}`}>
                                                            {err}
                                                        </div>
                                                    )}
                                                </td>
                                            );
                                        })}
                                        <td className="data-cell text-center sticky right-0 bg-card z-10">
                                            <button
                                                type="button"
                                                onClick={() => deleteRow(ri)}
                                                className="text-muted-foreground hover:text-destructive"
                                                title="Drop row"
                                                data-testid={`csv-editor-drop-${ri}`}
                                            >
                                                <X size={12} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {rows.length === 0 && (
                                <tr><td colSpan={fields.length + 2} className="p-6 text-center text-xs text-muted-foreground">No rows to import.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <DialogFooter className="flex items-center justify-between gap-2 pt-3 flex-shrink-0">
                    <div className="text-[11px] text-muted-foreground font-mono mr-auto">
                        {cleanRows} clean · {errorMap.size} with errors
                    </div>
                    <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="csv-editor-cancel-btn">Cancel</Button>
                    <Button
                        variant="outline" className="rounded-sm"
                        onClick={revalidate} disabled={validating || importing || rows.length === 0}
                        data-testid="csv-editor-validate-btn"
                    >
                        <ArrowsClockwise size={12} className="mr-1" /> {validating ? "Validating…" : "Validate"}
                    </Button>
                    <Button
                        className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground"
                        onClick={commit} disabled={importing || validating || errorCount > 0 || rows.length === 0}
                        data-testid="csv-editor-import-btn"
                    >
                        <UploadSimple size={12} className="mr-1" />
                        {importing ? "Importing…" : `Import ${rows.length} row${rows.length === 1 ? "" : "s"}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CellEditor({ field, value, onChange, invalid }) {
    const base = "w-full h-8 rounded-none border-0 border-l-2 bg-transparent text-xs px-2 focus:ring-0 focus:outline-none focus:border-primary";
    const cls = invalid ? `${base} border-l-destructive` : `${base} border-l-transparent`;
    const ft = field.field_type;

    if (ft === "dropdown" || ft === "radio") {
        const opts = field.options || [];
        return (
            <Select value={String(value ?? "")} onValueChange={onChange}>
                <SelectTrigger className={`${cls} rounded-none`} data-testid={`csv-cell-select-${field.field_name}`}>
                    <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                    {opts.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                </SelectContent>
            </Select>
        );
    }
    if (ft === "toggle" || ft === "checkbox") {
        const sval = String(value ?? "").toLowerCase();
        const norm = sval === "true" || sval === "1" || sval === "yes" || value === true ? "true" : (sval === "" ? "" : "false");
        return (
            <Select value={norm} onValueChange={(v) => onChange(v === "true")}>
                <SelectTrigger className={`${cls} rounded-none`}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="true">true</SelectItem>
                    <SelectItem value="false">false</SelectItem>
                </SelectContent>
            </Select>
        );
    }
    const inputType = ft === "number" || ft === "decimal" ? "number"
        : ft === "date" ? "date"
        : ft === "datetime" ? "datetime-local"
        : ft === "email" ? "email"
        : "text";

    return (
        <Input
            type={inputType}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className={cls}
            data-testid={`csv-cell-input-${field.field_name}`}
        />
    );
}
